import { AlertItem, AlertSeverity, AlertView } from "../models/Alerts/Alert";
import {
  CommanderActionContext,
  trackCommanderAction,
} from "./commanderTracking";
import { AlertsActionId } from "./catalog/alertsActions";

/** Where the click happened. Used in the event name and `surface` param. */
export type AlertsSurface = "bell" | "inbox" | "dtl";

/** Inbox stat tiles. */
export type AlertsStatTile = "unread" | "high" | "open";

/** Result of a submit-style action (sent as `outcome`). */
export type AlertsOutcome =
  "success" | "error" | "already_resolved" | "not_resolvable";

/** GA4 custom parameter string values are truncated around 100 chars. */
const GA_PARAM_STRING_MAX = 100;
const GA_EVENT_NAME_MAX = 40;

const SURFACE_LABELS: Record<AlertsSurface, string> = {
  bell: "Bell",
  inbox: "Inbox",
  dtl: "Detail",
};

const VIEW_LABELS: Record<AlertView, string> = {
  INBOX: "All",
  UNREAD: "Unread",
  OPEN: "Open",
  RESOLVED: "Resolved",
  ARCHIVED: "Archived",
};

const TILE_LABELS: Record<AlertsStatTile, string> = {
  unread: "Unread",
  high: "High priority",
  open: "Open",
};

const OUTCOME_LABELS: Record<AlertsOutcome, string> = {
  success: "resolved",
  error: "failed",
  already_resolved: "already resolved by someone else",
  not_resolvable: "not resolvable",
};

/** Short event-name part per action; the rest use the action id as-is. */
const EVENT_BASE: Partial<Record<AlertsActionId, string>> = {
  bell_open: "open",
  view_change: "view",
  section_change: "section",
  severity_change: "sev",
  stat_tile_click: "tile",
  page_change: "page",
};

export interface AlertsTrackExtra {
  /** View tab active for the action (or the one switched to). */
  view?: AlertView;
  /** Section filter active for the action; null = all sections. */
  section?: string | null;
  /** Severity filter active for the action; null = all severities. */
  severityFilter?: AlertSeverity | null;
  /** The alert acted on (row / detail actions). */
  alert?: Pick<
    AlertItem,
    "id" | "severity" | "section" | "type_code" | "status"
  >;
  /** Number of alerts affected (bulk actions). */
  count?: number;
  page?: number;
  tile?: AlertsStatTile;
  outcome?: AlertsOutcome;
  /** Row open: where the deep link went. */
  target?: "record" | "detail" | "group";
  /** Detail open_ref: header button or reference card. */
  placement?: "header" | "card";
  unreadCount?: number;
  highUnreadCount?: number;
}

const clip = (text: string): string => text.slice(0, GA_PARAM_STRING_MAX);

const plural = (count: number | undefined, noun: string): string => {
  const n = count ?? 0;
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
};

/**
 * Virtual screen: `Alerts › Bell`, `Alerts › Inbox › Unread`, `Alerts › Detail`.
 *
 * @param surface - Surface the action happened on.
 * @param view - Active view (inbox only).
 * @returns Screen name for GA params.
 */
export function alertsScreenName(
  surface: AlertsSurface,
  view?: AlertView,
): string {
  const base = `Alerts › ${SURFACE_LABELS[surface]}`;
  return surface === "inbox" && view ? `${base} › ${VIEW_LABELS[view]}` : base;
}

/**
 * Builds `alerts_{surface}_{action}[_{variant}]` (≤40 chars).
 *
 * @param actionId - Catalog action id.
 * @param surface - Surface code.
 * @param extra - Variant inputs (view / severity / tile).
 * @returns GA4 event name.
 */
export function alertsEventName(
  actionId: AlertsActionId,
  surface: AlertsSurface,
  extra?: AlertsTrackExtra,
): string {
  let variant = "";
  if (actionId === "view_change" && extra?.view) {
    variant = extra.view.toLowerCase();
  } else if (actionId === "severity_change") {
    variant = (extra?.severityFilter ?? "all").toLowerCase();
  } else if (actionId === "stat_tile_click" && extra?.tile) {
    variant = extra.tile;
  }
  const base = EVENT_BASE[actionId] ?? actionId;
  return `alerts_${surface}_${base}${variant ? `_${variant}` : ""}`.slice(
    0,
    GA_EVENT_NAME_MAX,
  );
}

/**
 * Readable action_name / action_description overrides per call.
 *
 * @param actionId - Catalog action id.
 * @param surface - Surface code.
 * @param extra - Label inputs.
 * @returns Overrides; undefined keeps the catalog default.
 */
function alertsActionLabels(
  actionId: AlertsActionId,
  surface: AlertsSurface,
  extra: AlertsTrackExtra,
): Pick<CommanderActionContext, "action_name" | "action_description"> {
  const where = SURFACE_LABELS[surface].toLowerCase();
  const section = extra.section ? ` · ${extra.section}` : "";
  const alert = extra.alert;
  const alertBrief = alert
    ? `${alert.severity} ${alert.type_code} (${alert.section})`
    : "";

  switch (actionId) {
    case "bell_open":
      return {
        action_name: "Opened alerts bell",
        action_description: clip(
          `Opened bell with ${plural(extra.unreadCount, "unread alert")}, ${extra.highUnreadCount ?? 0} high`,
        ),
      };
    case "view_change":
      return {
        action_name: `Switched to ${extra.view ? VIEW_LABELS[extra.view] : "view"} in ${where}`,
        action_description: undefined,
      };
    case "section_change":
      return {
        action_name: extra.section
          ? `Filtered section: ${extra.section}`
          : "Filtered section: All",
        action_description: undefined,
      };
    case "severity_change":
      return {
        action_name: `Filtered severity: ${extra.severityFilter ?? "All"}`,
        action_description: undefined,
      };
    case "stat_tile_click":
      return {
        action_name: `Clicked tile: ${extra.tile ? TILE_LABELS[extra.tile] : "?"}`,
        action_description: undefined,
      };
    case "mark_all_read":
      return {
        action_name: `Marked all read${section}`,
        action_description: clip(
          `Marked all read from ${where}${extra.section ? ` for section ${extra.section}` : " across all sections"}`,
        ),
      };
    case "bulk_read":
      return {
        action_name: `Marked ${plural(extra.count, "alert")} read`,
        action_description: undefined,
      };
    case "bulk_archive":
      return {
        action_name: `Archived ${plural(extra.count, "alert")}`,
        action_description: undefined,
      };
    case "bulk_unarchive":
      return {
        action_name: `Unarchived ${plural(extra.count, "alert")}`,
        action_description: undefined,
      };
    case "row_open":
      return {
        action_name: `Opened alert from ${where}`,
        action_description: clip(
          `${alertBrief} → ${
            extra.target === "detail"
              ? "alert detail (no route)"
              : extra.target === "group"
                ? "alert group"
                : "linked record"
          }`,
        ),
      };
    case "row_detail":
    case "row_read":
    case "row_archive":
    case "row_unarchive":
    case "archive":
    case "unarchive":
    case "resolve_open":
    case "resolve_cancel":
      return { action_name: undefined, action_description: clip(alertBrief) };
    case "open_ref":
      return {
        action_name: "Opened linked record",
        action_description: clip(
          `From detail ${extra.placement ?? "header"}: ${alertBrief}`,
        ),
      };
    case "resolve_submit":
      return {
        action_name: `Resolve ${OUTCOME_LABELS[extra.outcome ?? "success"]}`,
        action_description: clip(alertBrief),
      };
    case "page_change":
      return {
        action_name: `Inbox page → ${extra.page ?? "?"}`,
        action_description: undefined,
      };
    default:
      return { action_name: undefined, action_description: undefined };
  }
}

/**
 * Fires one GA4 event for an Alerts CTA. Non-blocking; never throws.
 *
 * @param actionId - Catalog action id.
 * @param surface - Where the click happened (bell / inbox / dtl).
 * @param extra - Filters, the alert acted on, counts, outcome.
 */
export function trackAlertsAction(
  actionId: AlertsActionId,
  surface: AlertsSurface,
  extra: AlertsTrackExtra = {},
): void {
  try {
    const { alert } = extra;
    const context: CommanderActionContext = {
      surface,
      screen_name: alertsScreenName(surface, extra.view),
      view: extra.view?.toLowerCase(),
      section_filter:
        extra.section === undefined ? undefined : (extra.section ?? "ALL"),
      severity_filter:
        extra.severityFilter === undefined
          ? undefined
          : (extra.severityFilter ?? "ALL"),
      alert_id: alert?.id,
      alert_severity: alert?.severity,
      alert_section: alert?.section,
      alert_type: alert?.type_code,
      alert_status: alert?.status,
      selected_count: extra.count,
      page: extra.page,
      tile: extra.tile,
      outcome: extra.outcome,
      target: extra.target,
      placement: extra.placement,
      unread_count: extra.unreadCount,
      high_unread_count: extra.highUnreadCount,
      ...alertsActionLabels(actionId, surface, extra),
    };

    trackCommanderAction({
      moduleId: "alerts",
      actionId,
      eventName: alertsEventName(actionId, surface, extra),
      context,
    });
  } catch {
    // never block UI
  }
}
